import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { HolidayPeriodDtoIn } from '@/models/in/HolidayPeriodDtoIn';
import { useDeleteAuthApi } from '@/hooks/UseApiCalls';

const QUERY_URL = 'management/holiday-periods';

interface DeleteHolidayPeriodDialogProps {
  open: boolean;
  onClose: () => void;
  period: HolidayPeriodDtoIn;
  onSuccess: () => void;
}

const DeleteHolidayPeriodDialog = ({
  open,
  onClose,
  period,
  onSuccess,
}: DeleteHolidayPeriodDialogProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const { mutate: deletePeriod, isPending: isDeleting } = useDeleteAuthApi<void>(
    `${QUERY_URL}/${period.id}`
  );

  const handleDelete = () => {
    setError(null);
    deletePeriod(undefined, {
      onSuccess: () => {
        onSuccess();
        onClose();
      },
      onError: (err) => {
        console.error('Error deleting holiday period:', err);
        setError(t('holidayPeriodsManagement.dialog.errorDeleting'));
      },
    });
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const message = error 
    ? `${t('holidayPeriodsManagement.confirmDelete', { name: period.name })}\n\n${error}`
    : t('holidayPeriodsManagement.confirmDelete', { name: period.name });

  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      title={t('holidayPeriodsManagement.delete')}
      message={message}
      confirmText={isDeleting ? 'saving' : 'holidayPeriodsManagement.deleteConfirm'}
      cancelText="holidayPeriodsManagement.deleteCancel"
      onConfirm={handleDelete}
      onCancel={handleClose}
      isLoading={isDeleting}
      variant="danger"
    />
  );
};

export default DeleteHolidayPeriodDialog;
